import React from 'react';
import Modal from 'react-bootstrap/Modal';
import axios from 'axios';
import authService from './api-authorization/AuthorizeService';
import { toast } from 'react-toastify';

interface JobQuoteExtraInvoiceItemProps {
    jobId: number;
    jobQuoteId: number;
    showModal: boolean;
    hideModal(): void;
    save(totalExtraInvoiceItem: number): void;
}

interface ExtraInvoiceItem {
    id: number;
    description: string;
    amount: number;
    invoiced: boolean;
}

const JobQuoteExtraInvoiceItemModal = (props: JobQuoteExtraInvoiceItemProps) => {
    const [loading, setLoading] = React.useState(false);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [extraInvoiceItems, setExtraInvoiceItems] = React.useState<ExtraInvoiceItem[]>([]);
    const [total, setTotal] = React.useState("$0.00");

    React.useEffect(() => {
        if (props.jobQuoteId > 0) {
            setLoading(true);
            getData();
        }
    }, [props.jobQuoteId]);

    const getData = async () => {
        const token = await authService.getAccessToken();

        axios.get('api/Job/GetJobQuoteExtraInvoiceItems?JobQuoteId=' + props.jobQuoteId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                var extraInvItems = res.data;
                var newRow = addBlankRow();
                extraInvItems.push(newRow);
                setExtraInvoiceItems(extraInvItems);
                setLoading(false);
                var total = calculateTotal(extraInvItems);
                var totalFormatted = total.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
                setTotal(totalFormatted);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const addBlankRow = () => {
        var newRow: ExtraInvoiceItem = {
            id: 0,
            description: "",
            amount: 0,
            invoiced: false
        };
        return newRow;
    }

    const handleCellChangeDesc = (e: any, itemIndex: number) => {
        //update item that has changed
        var items = [...extraInvoiceItems];
        var item = items[itemIndex];
        item.description = e.target.value;

        if (itemIndex == (items.length - 1)) {
            var newRow = addBlankRow();
            items.push(newRow);
        }

        setExtraInvoiceItems(items);
        //updateTotal
        var total = calculateTotal(items);
        var totalFormatted = total.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
        setTotal(totalFormatted);
    }

    const handleCellChangeValue = (e: any, itemIndex: number) => {
        //update item that has changed
        var items = [...extraInvoiceItems];
        var item = items[itemIndex];
        item.amount = e.target.value * 1;

        if (itemIndex == (items.length - 1)) {
            var newRow = addBlankRow();
            items.push(newRow);
        }

        setExtraInvoiceItems(items);
        //updateTotal
        var total = calculateTotal(items);
        var totalFormatted = total.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
        setTotal(totalFormatted);
    }

    const hideModal = () => {
        props.hideModal();
    }

    const deleteItem = (e: any, itemIndex: number) => {
        e.preventDefault();

        var items = [...extraInvoiceItems];
        var item = items[itemIndex];

        if (item.id == 0) {
            //just remove it from the grid as not saved
            items.splice(itemIndex, 1);
            setExtraInvoiceItems(items);
            var total = calculateTotal(items);
            var totalFormatted = total.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
            setTotal(totalFormatted);
            toast.success("Item removed successfully");
        } else {
            deleteInvoiceExtra(item.id, items, itemIndex)
        }
        
    }

    const deleteInvoiceExtra = async (itemId: number, items: ExtraInvoiceItem[], itemIndex: number) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var deleteInvItem = {
            id: itemId,
            jobId: props.jobId,
            subId: user.sub
        }

        axios.post('api/Job/DeleteJobQuoteExtraInvoiceItems', deleteInvItem, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Item removed successfully");
            items.splice(itemIndex, 1);
            setErrors({});
            setExtraInvoiceItems(items);
            var total = calculateTotal(items);
            var totalFormatted = total.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
            setTotal(totalFormatted);
        })
        .catch(error => {
            toast.error(error.message);
        });

    }

    const save = (e: any) => {
        e.preventDefault();

        saveInvoiceExtra();
    }

    const saveInvoiceExtra = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        extraInvoiceItems.pop();    //need to remove blank row
        var invItemsToSave = extraInvoiceItems.filter(e => !e.invoiced);

        var saveInvItems = {
            jobId: props.jobId,
            jobQuoteId: props.jobQuoteId,
            items: invItemsToSave,
            subId: user.sub
        }

        axios.post('api/Job/SaveJobQuoteExtraInvoiceItems', saveInvItems, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var total = calculateTotal(extraInvoiceItems);

            setErrors({});
            setExtraInvoiceItems([]);
            props.save(total);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const calculateTotal = (items: ExtraInvoiceItem[]) => {
        var total = 0;
        for (var i = 0; i < items.length; i++) {
            total += items[i].amount * 1;
        }
        return total;
    }

    const lastRowIndex = extraInvoiceItems.length - 1;
    
    const renderDetails = (
        <div>
            <Modal show={props.showModal} onHide={hideModal}>
                <Modal.Header className="modal__header--colour">
                    <Modal.Title>Add extra Initial Invoice Items</Modal.Title>
                    <button type="button" className="close" onClick={hideModal}>&times;</button>
                </Modal.Header>
                <Modal.Body>
                    <div>
                        <div>You can add extra items here that you want to appear on the Initial Invoice.</div>
                        <div>Note: Only use this screen if the Initial Invoice hasn't been created.</div>
                        <div>Note 2: These items will NOT be added to the scheduler for Logistics.</div>

                        <div className="overflowAuto">
                            <table className="table table__small tableColours">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Description</th>
                                        <th>Amount</th>
                                        <th>Invoiced</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                {extraInvoiceItems.map((item, index) =>
                                    <tbody key={index}>
                                        <tr key={index}>
                                            <td className="table__text--align">{index + 1}</td>
                                            <td className="table__text--align">
                                                <input type='text' maxLength={100} className="form-control" id="description" name="description" value={item.description} onChange={(e) => handleCellChangeDesc(e, index)} disabled={item.invoiced} />
                                            </td>
                                            <td className="table__text--align">
                                                <input type='number' min="0" step="0.01" className='form-control allocate__qtySize textalignright' id="amount" name="amount" value={item.amount} onChange={(e) => handleCellChangeValue(e, index)} disabled={item.invoiced} />
                                            </td>
                                            <td className="table__text--align">
                                                <input type='checkbox' checked={item.invoiced} readOnly disabled />
                                            </td>
                                            <td className="table__text--align">
                                                {lastRowIndex != index && !item.invoiced ?
                                                    <div className="delete--tablecell">
                                                        <a className="makeitred" href="#" onClick={(e) => deleteItem(e, index)}>
                                                            <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                                        </a>
                                                    </div> : ""
                                                }
                                            </td>
                                        </tr>
                                    </tbody>
                                )}
                            </table>
                            {errors["items"] ?
                                (errors["items"]).split("\n").map((item: any, key: any) => {
                                    return <span className="label errors" key={key}>{item}<br /></span>
                                })
                                : ""}
                        </div>
                        <div className="makeitflexright">
                            <div className="input-group--inline job__quote--total--nomargin">
                                <span className="label-large job__quote--total">Extra Invoice Amount</span>
                                <span className="job__quote--amount makeitflexright">{total}</span>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="popup-button--yes" onClick={save}>Save</button>
                </Modal.Footer>
            </Modal>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default JobQuoteExtraInvoiceItemModal;